/**
 * 23. 「このシーンを AI に相談」で添えるシーンの記録（テキスト、2000 字まで）
 *
 * 写真そのものは送らない。数値と利用者が書いた記録だけを要約する。
 * 位置情報（緯度・経度）は送らない（相談に要らないため）。
 */

import { committedSpots } from './scene.js';
import { computeShootResult, planLabel, filterCompStops } from './zone-calc.js';
import { getFilmById } from './data/films.js';
import { getDeveloperById } from './data/developers.js';
import { FILTER_FACTORS } from './presets.js';
import { ZONE_ROMAN } from './ui.js';
import { formatSeconds } from './data/dev-times.js';
import { dodgeBurnSummary } from './dodge-burn.js';

const EXP = { under: 'アンダー', good: '適正', over: 'オーバー' };
const CON = { 'too-flat': '軟調すぎ', 'as-planned': '計画どおり', 'too-contrasty': '硬調すぎ' };
const roman = (z) => ZONE_ROMAN[Math.max(0, Math.min(10, Math.round(z)))] ?? String(z);

export function buildSceneContext(shoot) {
  if (!shoot) return '';
  const lines = [];
  const film = getFilmById(shoot.filmId);
  const filter = FILTER_FACTORS[shoot.filterId];
  lines.push(`題名: ${shoot.title || '無題'}${shoot.location ? ` / 場所: ${shoot.location}` : ''}${shoot.weather ? ` / 天気・光: ${shoot.weather}` : ''}`);
  lines.push(`機材: ${shoot.camera || '不明'} ${shoot.filmFormat ? `(${shoot.filmFormat})` : ''} / レンズ: ${shoot.lens || '不明'}`);
  lines.push(`フィルム: ${film?.name || shoot.filmId} / EI ${shoot.ei} / フィルター: ${shoot.filterId && filter ? `${filter.name}（倍数 ${filter.stops} 段、露出への加算: ${filterCompStops(shoot) ? 'あり' : 'なし'}）` : 'なし'}`);
  if (shoot.templateId || shoot.sceneTemplate) lines.push(`シーンテンプレート: ${shoot.templateId || shoot.sceneTemplate}`);

  const spots = committedSpots(shoot);
  const r = computeShootResult({ ...shoot, spots });
  if (spots.length) {
    lines.push('測光ピン（EV は ISO 100 基準）:');
    r.spotsWithFallen.forEach((s, i) => {
      const role = s.role === 'shadow-anchor' ? '主要シャドウ' : s.role === 'highlight-anchor' ? '主要ハイライト' : '';
      const fallen = s.fallenZone != null ? ` → 落ちるゾーン ${roman(s.fallenZone)}` : '';
      lines.push(`  ${i + 1}. ${s.label || 'ラベルなし'}${role ? `【${role}】` : ''} EV ${Number(s.meterEV).toFixed(1)} / 置いたゾーン ${roman(s.placedZone)}${fallen}`);
    });
  }
  if (r.shootEV != null) {
    lines.push(`計算: 撮影 EV ${r.shootEV.toFixed(2)}（ISO 100）/ 現像方針 ${r.highlight ? planLabel(r.plan) : '未計算（主要ハイライトなし）'}${r.dynamicRange != null ? ` / ダイナミックレンジ ${r.dynamicRange.toFixed(1)} 段` : ''}`);
    if (r.primary) lines.push(`推奨露出: ${r.primary.aperture} · ${r.primary.shutter}`);
  }
  const x = shoot.shotExposure;
  if (x) lines.push(`撮影した露出（記録）: ${x.aperture} · ${x.shutter}${x.bracketStops ? ` / ブラケット ±${x.bracketStops} 段` : ''}`);

  const d = shoot.development;
  if (d) {
    const dev = getDeveloperById(d.developer);
    lines.push(`現像: ${dev ? `${dev.name} ${dev.dilution}` : d.developer || '不明'} / ${d.tempC ?? 20}°C / 実際 ${formatSeconds(d.actualDevTime || 0)}（目標 ${formatSeconds(d.targetDevTime || 0)}）${shoot.devAgitation ? ` / 攪拌 ${shoot.devAgitation}` : ''}`);
    if (d.exposureAccuracy || d.contrastResult) lines.push(`現像の評価: 露出 ${EXP[d.exposureAccuracy] || '未評価'} / コントラスト ${CON[d.contrastResult] || '未評価'}`);
    if (d.notes) lines.push(`現像メモ: ${d.notes}`);
  }
  const p = shoot.printing;
  if (p) {
    lines.push(`プリント: ${p.paper || '印画紙不明'} ${p.paperGrade || ''} / 基本露光 ${p.baseExposure || '不明'}`);
    const rt = [['シャドウ', p.shadowQuality], ['中間調', p.midtoneQuality], ['ハイライト', p.highlightQuality], ['総合', p.satisfaction]].filter(([, v]) => v != null);
    if (rt.length) lines.push(`プリントの評価（1〜5）: ${rt.map(([k, v]) => `${k} ${v}`).join(' / ')}`);
    if (p.dodgeBurnNotes) lines.push(`覆い焼き・焼き込み: ${p.dodgeBurnNotes}`);
    if (p.dodgeBurnMap?.length) lines.push(`覆い焼き・焼き込みの地図: ${dodgeBurnSummary(p.dodgeBurnMap)}`);
    if (p.lessonLearned) lines.push(`学んだこと: ${p.lessonLearned}`);
  }
  const env = shoot.environment;
  if (env) {
    const w = env.weather;
    lines.push(`撮影環境: ${w ? `${w.description || ''} ${w.tempC ?? ''}°C 雲量 ${w.cloudCover ?? '—'}%` : '天気不明'} / 太陽高度 ${env.sunAltitude ?? '—'}°`);
  }
  if (shoot.notes) lines.push(`メモ: ${shoot.notes}`);
  return lines.join('\n').slice(0, 2000);
}
